import * as types from './mutation-types';

import { first, indexOf, omit, sortedUniq } from 'lodash';

/* eslint no-param-reassign: "off" */
function addInteractionsToPanel(state, panelId, retrievedInteractions) {
  const updatedThreads = [];

  // Add all these interactions to our grand list of interactions
  state.interactions = retrievedInteractions.reduce((interactions, interaction) => {
    let currentInteraction = interaction;

    // Prevent losing metadata such as when we assign values for on air
    if (indexOf(Object.keys(interactions), String(interaction.id)) >= 0) {
      currentInteraction = Object.assign(state.interactions[interaction.id], interaction);
    }

    // Update thread counts
    const threadNotYetUpdated = updatedThreads.indexOf(interaction.threadId) === -1;
    const threadCountEntryExists = Object.prototype.hasOwnProperty.call(
      state.threadCounts,
      interaction.threadId,
    );
    // Only update if required
    // * if it doesn't yet have an entry
    // * if there is an entry and values are less we need to ignore means it's old data
    if (
      threadNotYetUpdated &&
      (!threadCountEntryExists ||
        (state.threadCounts[interaction.threadId].in <= interaction.inCount &&
          state.threadCounts[interaction.threadId].out <= interaction.outCount))
    ) {
      state.threadCounts = {
        ...state.threadCounts,
        [interaction.threadId]: {
          in: interaction.inCount,
          out: interaction.outCount,
        },
      };
      updatedThreads.push(interaction.threadId);
    }

    return { ...interactions, [interaction.id]: currentInteraction };
  }, state.interactions);

  const sortingKey = state.panelTypes[panelId] === 'onair' ? 'onAirId' : 'id';
  const isPaused = state.panelPaused[panelId];
  const interactionDest = isPaused ? 'panelPausedInteractions' : 'panelInteractions';

  // We now want to add only the id's of these interactions to the panel's
  // collection of ids
  state[interactionDest][panelId] = sortedUniq(
    state[interactionDest][panelId]
      .sort((a, b) => b[sortingKey] - a[sortingKey])
      .concat(retrievedInteractions.map(interaction => interaction.id)),
  );

  state.loadingInteractions[panelId] = false;
  state.loadingInteractionsSuccess[panelId] = true;

  // If we have any current threads push interactions into those.
  if (state.threadId && state.threadDialogVisible) {
    const interactionIdsInThreads = state.threads.map(i => i.id);
    const newThreadedInteractions = retrievedInteractions.filter(
      i => i.threadId === state.threadId && interactionIdsInThreads.indexOf(i.id) === -1,
    );
    state.threads = [...state.threads, ...newThreadedInteractions];
  }
}

export default {
  [types.GET_INTERACTIONS_REQUEST]: (state, panelId) => {
    state.loadingInteractions[panelId] = true;
    state.loadingInteractionsFailed[panelId] = false;
    state.loadingInteractionsSuccess[panelId] = false;
  },

  [types.GET_INTERACTIONS_FAILURE]: (state, [panelId, error]) => {
    state.loadingInteractions[panelId] = false;
    state.loadingInteractionsFailed[panelId] = true;
    state.loadingInteractionsError[panelId] = error;
  },

  [types.GET_INTERACTIONS_SUCCESS]: (state, [panelId, response, loadingMore]) => {
    if (loadingMore) {
      state.hasMoreInteractions[panelId] = !!response.next;
    }
    addInteractionsToPanel(state, panelId, response.results);
  },

  [types.NEW_INTERACTION_RECEIVED]: (state, interaction) => {
    Object.keys(state.panelSettings).forEach(panelId => {
      const panel = state.panelSettings[panelId];
      const panelType = state.panelTypes[panelId];

      const hasChannelFilter =
        Object.prototype.hasOwnProperty.call(panel, 'channel_ids') && panel.channel_ids.length > 0;
      const hasCampaignFilter =
        Object.prototype.hasOwnProperty.call(panel, 'campaign_ids') &&
        panel.campaign_ids.length > 0;

      const hasChannel =
        !hasChannelFilter ||
        (hasChannelFilter && panel.channel_ids.indexOf(interaction.channelId) !== -1);
      const hasCampaign =
        !hasCampaignFilter ||
        (hasCampaignFilter && panel.campaign_ids.indexOf(interaction.campaignId) !== -1);
      const matchFilters = panelType === 'campaign' && hasChannel && hasCampaign;
      const isOnAir = panelType === 'onair';
      const isActivity = panelType === 'activity';

      if (!isOnAir && (isActivity || matchFilters)) {
        addInteractionsToPanel(state, panelId, [interaction]);
      }
    });
  },

  [types.NEW_ONAIR_INTERACTION_RECEIVED]: (state, onairInteraction) => {
    const onAirPanelId = Object.keys(state.panelTypes).find(
      panelId => state.panelTypes[panelId] === 'onair',
    );
    const interaction = {
      ...onairInteraction.interaction,
      sentBy: `${onairInteraction.sentBy.firstName} ${onairInteraction.sentBy.lastName}`,
      onAirId: onairInteraction.id,
    };
    addInteractionsToPanel(state, onAirPanelId, [interaction]);
  },

  [types.GET_INTERACTION_THREAD_REQUEST]: state => {
    state.loadingThreads = true;
    state.loadingThreadsFailed = false;
    state.loadingThreadsSuccess = false;
  },

  [types.GET_INTERACTION_THREAD_FAILURE]: (state, error) => {
    state.loadingThreads = false;
    state.loadingThreadsFailed = true;
    state.loadingThreadsError = error;
  },

  [types.GET_INTERACTION_THREAD_SUCCESS]: (state, response) => {
    state.loadingThreads = false;
    state.loadingThreadsFailed = false;
    state.loadingThreadsSuccess = true;
    state.threads = response;
  },

  [types.CLEAR_THREADS]: state => {
    state.threads = [];
  },

  [types.APPEND_THREAD_REPLY]: (state, { interactionId, reply }) => {
    state.threads = state.threads.map(interaction => {
      if (interaction.id !== interactionId) {
        return interaction;
      }

      if (!Object.prototype.hasOwnProperty.call(interaction.ore, 'reply')) {
        interaction.ore.reply = [];
      }
      interaction.ore.reply.push(reply);

      const currentCount = state.threadCounts[interaction.thread];
      state.threadCounts = {
        ...state.threadCounts,
        [interaction.thread]: { in: currentCount.in, out: currentCount.out + 1 },
      };

      return interaction;
    });
  },

  [types.SET_THREAD_ID]: (state, threadId) => {
    state.threadId = threadId;
  },

  [types.SET_THREAD_CHANNEL_TYPE]: (state, channelType) => {
    state.threadChannel = channelType;
  },

  [types.SET_THREAD_DIALOG_VISIBLE]: (state, isVisible) => {
    state.threadDialogVisible = isVisible;
  },

  [types.SET_MEDIA_IS_PLAYING]: (state, mediaIsPlaying) => {
    state.mediaIsPlaying = mediaIsPlaying;
  },

  [types.CLEAR_PANEL_INTERACTIONS]: (state, panelId) => {
    state.panelInteractions[panelId] = null;
    state.hasMoreInteractions[panelId] = true;
  },

  [types.CLEAR_PANEL_HAS_MORE_INDICATOR]: (state, panelId) => {
    state.hasMoreInteractions[panelId] = true;
  },

  [types.SEARCH_INTERACTIONS_SUCCESS]: (state, [panelId, page, response]) => {
    state.hasMoreInteractions[panelId] = response.count > 0 && response.results.length > 0;
    const retrievedInteractions = response.results;
    // Add all these interactions to our grand list of interactions
    const updatedThreads = [];
    state.interactions = retrievedInteractions.reduce((interactions, interaction) => {
      let currentInteraction = interaction;

      // Prevent losing metadata such as when we assign values for on air
      if (indexOf(Object.keys(interactions), String(interaction.id)) >= 0) {
        currentInteraction = Object.assign(state.interactions[interaction.id], interaction);
      }

      // Update thread counts
      const threadNotYetUpdated = updatedThreads.indexOf(interaction.threadId) === -1;
      const threadCountEntryExists = Object.prototype.hasOwnProperty.call(
        state.threadCounts,
        interaction.threadId,
      );
      // Only update if required
      // * if it doesn't yet have an entry
      // * if there is an entry and values are less we need to ignore means it's old data
      if (
        threadNotYetUpdated &&
        (!threadCountEntryExists ||
          (state.threadCounts[interaction.threadId].in <= interaction.inCount &&
            state.threadCounts[interaction.threadId].out <= interaction.outCount))
      ) {
        state.threadCounts = {
          ...state.threadCounts,
          [interaction.threadId]: {
            in: interaction.inCount,
            out: interaction.outCount,
          },
        };
        updatedThreads.push(interaction.threadId);
      }

      return Object.assign(interactions, {
        [interaction.id]: currentInteraction,
      });
    }, state.interactions);

    if (page === 1) {
      state.panelInteractions[panelId] = [];
    }

    // We now want to add only the id's of these interactions to the panel's
    // collection of ids
    state.panelInteractions[panelId] = sortedUniq(
      state.panelInteractions[panelId]
        .concat(retrievedInteractions.map(interaction => interaction.id))
        .sort((a, b) => b - a),
    );
    state.loadingInteractions[panelId] = false;
    state.loadingInteractionsSuccess[panelId] = true;
  },

  [types.GET_SINGLE_INTERACTION_SUCCESS]: (state, response) => {
    state.interactions = Object.assign({}, state.interactions, { [response.id]: response });
  },

  [types.SET_INITIAL_PANEL_PAGE]: (state, panelId) => {
    state.panelPages[panelId] = 1;
  },

  [types.INCREMENT_PANEL_PAGE]: (state, panelId) => {
    state.panelPages[panelId] += 1;
  },

  [types.DECREMENT_PANEL_PAGE]: (state, panelId) => {
    state.panelPages[panelId] -= 1;
  },

  [types.GET_PANELS_REQUEST]: state => {
    state.loadingPanels = true;
    state.loadingPanelsFailed = false;
    state.loadingPanelsSuccess = false;
  },

  [types.GET_PANELS_FAILURE]: (state, error) => {
    state.loadingPanels = false;
    state.loadingPanelsFailed = true;
    state.loadingPanelsError = error;
  },

  [types.ADD_MANUAL_PANEL]: (state, panel) => {
    state.panels = { [panel.uuid]: panel.settings.name, ...state.panels };
    state.panelSettings = { [panel.uuid]: panel.settings, ...state.panelSettings };
    state.panelTypes = { [panel.uuid]: panel.type, ...state.panelTypes };
    state.panelPages = { [panel.uuid]: 1, ...state.panelPages };
    state.panelInteractions = { [panel.uuid]: [], ...state.panelInteractions };
  },

  [types.REMOVE_MANUAL_PANEL]: (state, panel) => {
    state.panels = omit(state.panels, panel.uuid);
    state.panelSettings = omit(state.panelSettings, panel.uuid);
    state.panelInteractions = omit(state.panelInteractions, panel.uuid);
    state.panelTypes = omit(state.panelTypes, panel.uuid);
    state.panelPages = omit(state.panelPages, panel.uuid);
  },

  [types.GET_PANELS_SUCCESS]: (state, response) => {
    // Create panel declarations
    state.panels = response.settings.reduce(
      (titles, panel) => Object.assign({}, titles, { [panel.uuid]: panel.settings.name }),
      state.panels,
    );

    // Assign settings such as filters, colours and names
    state.panelSettings = response.settings.reduce(
      (settings, panel) => Object.assign({}, settings, { [panel.uuid]: panel.settings }),
      state.panelSettings,
    );

    // Assign types
    state.panelTypes = response.settings.reduce(
      (panelTypes, panel) => Object.assign({}, panelTypes, { [panel.uuid]: panel.type }),
      state.panelTypes,
    );

    // Initialize panel page
    state.panelPages = Object.keys(state.panels).reduce(
      (panelPages, panelId) => ({
        ...panelPages,
        [panelId]: 1,
      }),
      {},
    );

    // Initialize with blank interaction data
    state.panelInteractions = Object.keys(state.panels).reduce(
      (panelInteractions, panelId) => Object.assign({}, panelInteractions, { [panelId]: [] }),
      state.panelInteractions,
    );

    state.loadingPanels = false;
    state.loadingPanelsSuccess = true;
  },

  [types.UPDATE_PANEL_ORDER]: (state, panels) => {
    state.panels.settings = panels;
  },

  [types.UPDATE_PANEL_SETTINGS]: state => {
    state.updatingPanelSettings = true;
    state.updatePanelSettingsSuccess = false;
    state.updatePanelSettingsFailed = false;
  },
  [types.UPDATE_PANEL_SETTINGS_FAILURE]: state => {
    state.updatingPanelSettings = false;
    state.updatePanelSettingsFailed = true;
  },
  [types.UPDATE_PANEL_SETTINGS_SUCCESS]: (state, [panelId, response]) => {
    state.updatingPanelSettings = false;
    state.updatePanelSettingsSuccess = true;

    const updatedPanel = first(response.settings.filter(panel => panel.uuid === panelId));

    state.panels[panelId] = updatedPanel.settings.name;
    state.panelSettings[panelId] = updatedPanel.settings;
    state.panelPages[panelId] = 1;
    state.panelInteractions[panelId] = [];
  },

  [types.CREATE_PANEL_REQUEST]: state => {
    state.creatingPanel = true;
    state.createPanelFailed = false;
  },
  [types.CREATE_PANEL_FAILURE]: state => {
    state.creatingPanel = false;
    state.createPanelFailed = true;
  },
  [types.CREATE_PANEL_SUCCESS]: (state, panel) => {
    state.creatingPanel = false;

    // Construct initial panel data
    state.panels = Object.assign({}, state.panels, { [panel.uuid]: panel.settings.name });
    state.panelSettings = Object.assign({}, state.panelSettings, {
      [panel.uuid]: panel.settings || {},
    });
    state.panelInteractions = Object.assign({}, state.panelInteractions, { [panel.uuid]: [] });
    state.panelPages = Object.assign({}, state.panelPages, { [panel.uuid]: 1 });
    state.panelTypes = Object.assign({}, state.panelTypes, { [panel.uuid]: panel.type });
  },

  [types.DELETE_PANEL_REQUEST]: state => {
    state.deletingPanel = true;
    state.deletePanelFailed = false;
  },
  [types.DELETE_PANEL_FAILURE]: state => {
    state.deletingPanel = false;
    state.deletePanelFailed = true;
  },
  [types.DELETE_PANEL_SUCCESS]: (state, panelId) => {
    state.deletingPanel = false;

    state.panels = omit(state.panels, panelId);
    state.panelSettings = omit(state.panelSettings, panelId);
    state.panelInteractions = omit(state.panelInteractions, panelId);
    state.panelPages = omit(state.panelPages, panelId);
  },

  [types.SET_GALLERY_IMAGES]: (state, images) => {
    state.galleryImages = images;
  },

  [types.SET_GALLERY_VISIBLE]: (state, isVisible) => {
    state.galleryVisible = isVisible;
  },

  [types.SET_GALLERY_INDEX]: (state, index) => {
    state.galleryIndex = index;
  },

  [types.PAUSE_PANEL]: (state, panelId) => {
    state.panelPaused = { ...state.panelPaused, [panelId]: true };
    state.panelPausedInteractions = { ...state.pausedInteractions, [panelId]: [] };
  },

  [types.UNPAUSE_PANEL]: (state, panelId) => {
    state.panelPaused = { ...state.panelPaused, [panelId]: false };

    if (!Object.prototype.hasOwnProperty.call(state.panelPausedInteractions, panelId)) {
      state.panelPausedInteractions = { ...state.pausedInteractions, [panelId]: [] };
    }

    const pausedInteractions = state.panelPausedInteractions[panelId];
    const sortingKey = state.panelTypes[panelId] === 'onair' ? 'onAirId' : 'id';

    if (!Array.isArray(state.panelInteractions[panelId])) {
      state.panelInteractions[panelId] = [];
    }

    state.panelInteractions[panelId] = sortedUniq(
      state.panelInteractions[panelId]
        .sort((a, b) => b[sortingKey] - a[sortingKey])
        .concat(pausedInteractions),
    );

    state.panelPausedInteractions = { ...state.pausedInteractions, [panelId]: [] };
  },

  [types.CLEAR_PANEL_PAUSED]: (state, panelId) => {
    state.panelPaused = omit(state.panelPaused, panelId);
  },

  [types.CLEAR_DASHBOARD_PANEL_PAUSED]: state => {
    state.panelPaused = {};
  },

  [types.SET_PANEL_SETTINGS_DIALOG]: (state, settings) => {
    state.panelSettingsDialog = settings;
  },

  [types.SET_PANEL_SETTINGS_DIALOG_VISIBLE]: (state, isVisible) => {
    state.panelSettingsDialogVisible = isVisible;
  },

  [types.DELETE_INTERACTION_SUCCESS]: (state, [panelId, interactionId]) => {
    state.panelInteractions[panelId] = state.panelInteractions[panelId].filter(
      id => id !== interactionId,
    );
  },

  [types.GET_SEARCH_RESULTS_REQUEST]: state => {
    state.searching = true;
  },
  [types.GET_SEARCH_RESULTS_SUCCESS]: (state, response) => {
    state.searchResults = response.results;
    state.searchCount = response.count;
    state.searching = false;
  },

  [types.GET_SEARCH_FIELDS_REQUEST]: state => {
    state.searchFieldsLoading = true;
    state.searchFieldsFailure = false;
    state.searchFieldsSuccess = false;
  },

  [types.GET_SEARCH_FIELDS_SUCCESS]: (state, response) => {
    state.searchFields = response;
    state.searchFieldsSuccess = true;
    state.searchFieldsLoading = false;
  },

  [types.GET_SEARCH_FIELDS_FAILURE]: (state, error) => {
    state.searchFieldsFailure = true;
    state.searchFieldsLoading = false;
    state.searchFieldsError = error;
  },

  [types.SET_SEARCH_TERM]: (state, term) => {
    state.searchTerm = term;
  },

  [types.GET_TWITTER_TRENDS_REQUEST]: state => {
    state.fetchingTwitterTrends = true;
    state.fetchingTwitterTrendsSuccess = false;
    state.fetchingTwitterTrendsFailed = false;
  },

  [types.GET_TWITTER_TRENDS_SUCCESS]: (state, data) => {
    state.fetchingTwitterTrends = false;
    state.fetchingTwitterTrendsSuccess = true;
    state.twitterTrends = data;
  },

  [types.GET_TWITTER_TRENDS_FAILURE]: (state, error) => {
    state.fetchingTwitterTrends = false;
    state.fetchingTwitterTrendsFailed = true;
    state.twitterTrendsError = error;
  },

  [types.EXPORT_TO_FILE_REQUEST]: state => {
    state.loadingExportFiles = true;
  },

  [types.EXPORT_TO_FILE_SUCCESS]: state => {
    state.loadingExportFiles = false;
  },

  [types.EXPORT_TO_FILE_FAILURE]: state => {
    state.loadingExportFiles = false;
  },
  [types.UPDATE_PANEL_ORDER_REQUEST]: (state, panels) => {
    state.panels = panels;
  },
  [types.UPDATE_PANEL_ORDER_SUCCESS]: state => {
    state.updatePanelOrderSuccess = true;
    state.updatePanelOrderFailure = false;
  },
  [types.UPDATE_PANEL_ORDER__FAILURE]: state => {
    state.updatePanelOrderFailure = true;
    state.updatePanelOrderSuccess = false;
  },
  [types.GET_INSTANT_POLL_STATS_SUCCESS]: (state, { stats, panelId }) => {
    state.instantPollResults = { ...state.instantPollResults, [panelId]: stats };
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/interaction/mutations.js